import { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { MdCheckCircleOutline } from "react-icons/md";
import banner1 from "../assets/Waterproofing/banner1.jpeg";
import banner2 from "../assets/Waterproofing/banner2.jpg";
import banner3 from "../assets/Waterproofing/banner3.jpg";
import banner4 from "../assets/Waterproofing/banner4.jpg";
import banner5 from "../assets/Waterproofing/banner5.jpg";
import banner6 from "../assets/Waterproofing/banner6.jpg";
import banner7 from "../assets/Waterproofing/banner7.jpg";
import banner8 from "../assets/Waterproofing/banner8.jpg";

export default function WaterproofingSolutions() {
  // Independent image sets for each slider
  const slider1Images = [banner1, banner2, banner3, banner4];
  const slider2Images = [banner5, banner6, banner7, banner8];

  // Independent state for each slider
  const [slider1Index, setSlider1Index] = useState(0);
  const [slider2Index, setSlider2Index] = useState(0);

  const problems = [
    "Recurring roof leaks during heavy rains",
    "Water seepage through basements and foundations",
    "Cracked or porous concrete allowing moisture penetration",
    "Bathroom and wet-area leakage affecting lower floors",
    "Swimming pool and water-retaining structure failures",
    "Damp walls and peeling finishes caused by rising moisture",
  ];

  const systems = [
    {
      title: "Membrane Systems",
      detail:
        "Large roof decks, podium slabs and exposed surfaces requiring high durability and mechanical resistance.",
    },
    {
      title: "Liquid-Applied Waterproofing",
      detail:
        "Seamless coatings for balconies, wet areas, and detailing challenges.",
    },
    {
      title: "Fibre-Reinforced Systems",
      detail:
        "Fibre mesh reinforcement to enhance durability and accommodate structural movement.",
    },
    {
      title: "Cementitious Waterproofing",
      detail:
        "Basements, foundations, retaining walls, swimming pools, and water-retaining structures with hydrostatic pressure resistance.",
    },
  ];

  const applications = [
    "Roofs (flat and pitched)",
    "Foundations and basements",
    "Bathrooms and wet areas",
    "Swimming pools",
    "Retaining walls",
    "Exposed concrete decks and terraces",
  ];

  const benefits = [
    "Reduced long-term maintenance costs",
    "Protection of reinforcement and structural components",
    "Elimination of recurring leak repairs",
    "Increased property durability and asset value",
    "Confidence during heavy rainfall seasons",
  ];

  const approachSteps = [
    {
      title: "Assessment Before Application",
      detail:
        "We evaluate substrate condition, drainage performance, exposure level, and structural movement before selecting the right system.",
    },
    {
      title: "Correct Surface Preparation",
      detail:
        "Cleaning, crack treatment, priming, and reinforcement detailing are executed thoroughly to ensure long-term adhesion.",
    },
    {
      title: "Detail-Oriented Application",
      detail:
        "Critical zones such as corners, pipe penetrations, expansion joints, and upstands receive reinforced treatment to avoid repeat failures.",
    },
  ];

  // Initialize AOS
  useEffect(() => {
    AOS.init({
      duration: 700,
      easing: "ease-out-cubic",
      once: false,
      offset: 80,
    });
    AOS.refresh();
  }, []);

  // Slider 1: Fade transition (3.5s interval)
  useEffect(() => {
    if (slider1Images.length <= 1) return;
    const timer = setInterval(() => {
      setSlider1Index((prev) => (prev + 1) % slider1Images.length);
    }, 3500);
    return () => clearInterval(timer);
  }, [slider1Images.length]);

  // Slider 2: Horizontal scroll transition (5s interval)
  useEffect(() => {
    if (slider2Images.length <= 1) return;
    const timer = setInterval(() => {
      setSlider2Index((prev) => (prev + 1) % slider2Images.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slider2Images.length]);

  const IconList = ({ items, boldTitle = false }) => (
    <ul className="wp-icon-list">
      {items.map((item) => (
        <li
          key={boldTitle ? item.title : item}
          className="wp-icon-list-item"
        >
          <MdCheckCircleOutline size={19} className="wp-list-icon" />
          <span>
            {boldTitle ? <strong>{item.title}:</strong> : null}{" "}
            {boldTitle ? item.detail : item}
          </span>
        </li>
      ))}
    </ul>
  );

  return (
    <section className="MainBodySection py-5 mt-5 wp-page">
      <div className="container">
        <div className="row g-3">
          <div className="col-md-7 d-flex" data-aos="fade-up">
            <div className="rounded mShadow2 bg-light p-3 wp-equal-card w-100">
              <div className="text-primary font-bold">
                <p className="text-primary font-bold fs-3">
                  WATERPROOFING SOLUTIONS
                </p>
              </div>
              <div className="text-muted font-semi-bold">
                <p>Stop Water Damage Before It Compromises Your Structure</p>
              </div>
              <p>
                Water intrusion is rarely visible at first but its impact is
                severe. Leaking roofs, rising damp, cracked terraces and failing
                bathrooms lead to structural deterioration, corrosion of
                reinforcement, mold growth and expensive remedial works.
                <br />
                We provide professional waterproofing systems designed to
                safeguard your building against leaks, seepage, and structural
                deterioration.
                <br />
                Whether for new construction or remedial intervention, our
                solutions are designed to protect structural integrity, prevent
                recurring failure and extend the service life of your building.
              </p>
            </div>
          </div>

          {/* Slider 1: Fade Transition */}
          <div
            className="col-md-5 d-flex"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <div className="w-100 wp-slider-container-1">
              {slider1Images.map((image, idx) => (
                <div
                  key={`slider1-${idx}`}
                  className={`wp-slider-slide-1 ${
                    idx === slider1Index ? "active" : ""
                  }`}
                  style={{ backgroundImage: `url(${image})` }}
                  role="img"
                  aria-label={`Waterproofing slider 1 image ${idx + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Slider 2: Horizontal Scroll Transition */}
          <div
            className="col-md-5 mt-3 d-flex"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <div className="w-100 wp-slider-container-2">
              <div
                className="wp-slider-track-2"
                style={{ transform: `translateX(-${slider2Index * 100}%)` }}
              >
                {slider2Images.map((image, idx) => (
                  <div
                    key={`slider2-${idx}`}
                    className="wp-slider-slide-2"
                    style={{ backgroundImage: `url(${image})` }}
                    role="img"
                    aria-label={`Waterproofing slider 2 image ${idx + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="col-md-7 mt-3 d-flex" data-aos="fade-up">
            <div className="rounded mShadow2 bg-light p-3 w-100 wp-equal-card">
              <div className="text-primary font-bold">
                <p>The Problems We Solve:</p>
              </div>
              <IconList items={problems} />
            </div>
          </div>
          <div
            className="col-md-12 bg-surface rounded p-2 text-center my-5"
            data-aos="fade-up"
          >
            <div className="text-primary font-bold">
              <p
                className="font-bold text-primary fs-3"
                style={{ color: "var(--primaryColor) !important" }}
              >
                Our Waterproofing Systems
              </p>
            </div>
            <div className="text-muted font-semi-bold">
              <p className="text-light">
                We deploy system-based waterproofing solutions tailored to
                exposure level, structural movement and substrate condition:
              </p>
            </div>
          </div>
          {systems.map((system) => (
            <div
              className="col-md-6 p-2 mt-2 d-flex"
              key={system.title}
              data-aos="fade-up"
            >
              <div className="bg-light rounded p-3 text-center mShadow2 wp-equal-card w-100">
                <div className="text-primary font-bold">
                  <p className="font-semi-bold">{system.title}</p>
                </div>
                <p>{system.detail}</p>
              </div>
            </div>
          ))}
          <div className="col-md-12 mt-5" data-aos="fade-up">
            <p className="text-primary text-center font-bold mb-1">
              Our Approach
            </p>
            <p className="text-center text-muted mb-2">
              We follow a practical 3-step process to deliver durable
              waterproofing performance.
            </p>
            <div className="row">
              {approachSteps.map((step, index) => (
                <div
                  className="col-md-4 p-2 d-flex"
                  key={step.title}
                  data-aos="fade-up"
                >
                  <div className="h-100 bg-light rounded p-3 wp-equal-card w-100 wp-approach-card">
                    <div className="wp-step-pill">{`Step ${index + 1}`}</div>
                    <div className="text-primary font-bold mt-2">
                      <p className="font-semi-bold mb-1">{step.title}</p>
                    </div>
                    <p className="mb-0">{step.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="col-md-6 d-flex" data-aos="fade-up">
            <div className="w-100 bg-light rounded mShadow2 p-3 wp-equal-card">
              <div className="text-primary font-bold">
                <p className="font-semi-bold">Where It works Best</p>
              </div>
              <IconList items={applications} />
            </div>
          </div>
          <div className="col-md-6 d-flex" data-aos="fade-up">
            <div className="w-100 bg-light rounded mShadow2 p-3 wp-equal-card">
              <div className="text-primary font-bold">
                <p className="font-semi-bold">What this means for you:</p>
              </div>
              <IconList items={benefits} />
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .wp-page {
          background: radial-gradient(
              circle at top left,
              rgba(13, 110, 253, 0.08),
              transparent 45%
            ),
            #f8fafc;
        }

        .wp-equal-card {
          display: flex;
          flex-direction: column;
          height: 100%;
          border-radius: 16px;
          box-shadow: 0 12px 30px rgba(15, 23, 42, 0.08);
          border: 1px solid rgba(148, 163, 184, 0.18);
        }

        /* ========== Slider 1: Fade Transition ========== */
        .wp-slider-container-1 {
          position: relative;
          min-height: 230px;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.14);
          border: 1px solid rgba(148, 163, 184, 0.2);
        }

        .wp-slider-slide-1 {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          opacity: 0;
          transition: opacity 0.8s ease-in-out;
        }

        .wp-slider-slide-1.active {
          opacity: 1;
          z-index: 1;
        }

        /* ========== Slider 2: Horizontal Scroll Transition ========== */
        .wp-slider-container-2 {
          position: relative;
          min-height: 230px;
          border-radius: 18px;
          overflow: hidden;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.14);
          border: 1px solid rgba(148, 163, 184, 0.2);
        }

        .wp-slider-track-2 {
          display: flex;
          height: 100%;
          transition: transform 0.8s cubic-bezier(0.25, 1, 0.5, 1);
        }

        .wp-slider-slide-2 {
          min-width: 100%;
          height: 100%;
          background-size: cover;
          background-position: center;
          flex-shrink: 0;
        }

        .wp-icon-list {
          margin: 0;
          padding: 0;
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .wp-icon-list-item {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          line-height: 1.6;
          color: #1f2937;
        }

        .wp-list-icon {
          color: #0f766e;
          margin-top: 2px;
          flex-shrink: 0;
        }

        .wp-approach-card {
          border: 1px solid rgba(15, 118, 110, 0.2);
          box-shadow: 0 10px 24px rgba(15, 118, 110, 0.1);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .wp-approach-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 16px 34px rgba(15, 118, 110, 0.16);
        }

        .wp-step-pill {
          width: fit-content;
          border-radius: 999px;
          font-size: 12px;
          padding: 4px 10px;
          color: #0f766e;
          background: rgba(15, 118, 110, 0.12);
          border: 1px solid rgba(15, 118, 110, 0.22);
          font-family: "FontSemiBold", "Segoe UI", sans-serif;
          letter-spacing: 0.02em;
        }

        .wp-page .text-primary {
          color: #0f172a !important;
        }

        .wp-page .text-muted {
          color: #475569 !important;
        }

        @media (max-width: 767px) {
          .wp-slider-container-1,
          .wp-slider-container-2 {
            min-height: 160px;
          }
        }
      `}</style>
    </section>
  );
}